import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, Minus, Maximize2, Minimize2, RotateCcw, ChevronUp } from "lucide-react"

import { cn } from "@/lib/utils"

/* Shared window context: lets DialogContent switch the Root's modal behavior while minimized */
const WinCtx = React.createContext(null)

const Dialog = ({ children, onOpenChange, ...props }) => {
  const [minimized, setMinimized] = React.useState(false)
  const handleOpenChange = React.useCallback((o) => {
    if (!o) setMinimized(false)
    onOpenChange?.(o)
  }, [onOpenChange])
  const ctx = React.useMemo(() => ({ minimized, setMinimized }), [minimized])
  return (
    <WinCtx.Provider value={ctx}>
      <DialogPrimitive.Root {...props} modal={!minimized} onOpenChange={handleOpenChange}>
        {children}
      </DialogPrimitive.Root>
    </WinCtx.Provider>
  )
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[9999] bg-black/60 overflow-auto flex items-end sm:items-center justify-center [-webkit-overflow-scrolling:touch] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "p-0 sm:p-6",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const MIN_W = 320
const MIN_H = 180

const clampToViewport = (x, y, rect) => {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const margin = 60
  const maxX = vw / 2 + (rect?.width ?? 0) / 2 - margin
  const maxY = vh / 2 + (rect?.height ?? 0) / 2 - margin
  return {
    x: Math.max(Math.min(x, maxX), -maxX),
    y: Math.max(Math.min(y, maxY), -maxY),
  }
}

const loadPersisted = (persistId) => {
  if (!persistId) return null
  try {
    const raw = localStorage.getItem(`win:${persistId}`)
    if (!raw) return null
    const v = JSON.parse(raw)
    if (typeof v !== "object") return null
    return v
  } catch { return null }
}

const savePersisted = (persistId, state) => {
  if (!persistId) return
  try { localStorage.setItem(`win:${persistId}`, JSON.stringify(state)) } catch { /* */ }
}

const DialogContent = React.forwardRef(({ className, children, persistId, preventClose, hideWindowControls, style, onOpenAutoFocus, onEscapeKeyDown, onInteractOutside, onPointerDownOutside, ...props }, ref) => {
  const win = React.useContext(WinCtx)
  const minimized = win?.minimized || false
  const [maximized, setMaximized] = React.useState(false)
  const [offset, setOffset] = React.useState({ x: 0, y: 0 })
  const [size, setSize] = React.useState(null) // {w,h} or null = natural
  const panelRef = React.useRef(null)
  const gesture = React.useRef(null)
  const titleRef = React.useRef("")
  const isRtl = typeof document !== "undefined" && document.documentElement.dir === "rtl"

  // Restore persisted geometry on mount
  React.useEffect(() => {
    const saved = loadPersisted(persistId)
    if (!saved || window.innerWidth < 768) return
    const vw = window.innerWidth, vh = window.innerHeight
    let next = {}
    if (saved.w && saved.h) {
      next.size = { w: Math.min(Math.max(saved.w, MIN_W), vw - 24), h: Math.min(Math.max(saved.h, MIN_H), vh - 24) }
    }
    if (typeof saved.x === "number" && typeof saved.y === "number") {
      // If saved position would push the window off-screen, reset to center
      if (Math.abs(saved.x) < vw / 2 && Math.abs(saved.y) < vh / 2) next.offset = { x: saved.x, y: saved.y }
    }
    if (next.size) setSize(next.size)
    if (next.offset) setOffset(next.offset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistId])

  const persist = React.useCallback((o, s) => {
    if (!persistId) return
    savePersisted(persistId, { x: o?.x ?? 0, y: o?.y ?? 0, w: s?.w, h: s?.h })
  }, [persistId])

  // capture dialog title text for the minimized pill
  React.useEffect(() => {
    if (!minimized) return
    // blur any focused element inside the hidden panel
    if (panelRef.current && panelRef.current.contains(document.activeElement)) {
      document.activeElement.blur?.()
    }
  }, [minimized])

  const startDrag = (e) => {
    if (window.innerWidth < 768 || maximized || minimized) return
    if (e.button !== 0 && e.pointerType === "mouse") return
    // don't hijack interactive elements inside the drag strip
    if (e.target.closest("button, input, select, textarea, a, [role='tab'], [role='combobox']")) return
    e.preventDefault()
    gesture.current = { type: "drag", startX: e.clientX, startY: e.clientY, baseX: offset.x, baseY: offset.y }
    window.addEventListener("pointermove", onGestureMove)
    window.addEventListener("pointerup", endGesture, { once: true })
  }

  const startResize = (e) => {
    if (window.innerWidth < 768 || maximized || minimized) return
    e.preventDefault()
    e.stopPropagation()
    const rect = panelRef.current?.getBoundingClientRect()
    gesture.current = { type: "resize", startX: e.clientX, startY: e.clientY, baseW: rect?.width || 500, baseH: rect?.height || 400 }
    window.addEventListener("pointermove", onGestureMove)
    window.addEventListener("pointerup", endGesture, { once: true })
  }

  const onGestureMove = (e) => {
    const g = gesture.current
    if (!g) return
    if (g.type === "drag") {
      const rect = panelRef.current?.getBoundingClientRect()
      const nx = g.baseX + (e.clientX - g.startX)
      const ny = g.baseY + (e.clientY - g.startY)
      const c = clampToViewport(nx, ny, rect)
      setOffset(c)
    } else if (g.type === "resize") {
      const dirFactor = isRtl ? -1 : 1
      const w = Math.min(Math.max(g.baseW + dirFactor * (e.clientX - g.startX), MIN_W), window.innerWidth - 24)
      const h = Math.min(Math.max(g.baseH + (e.clientY - g.startY), MIN_H), window.innerHeight - 24)
      setSize({ w, h })
    }
  }

  const geomRef = React.useRef({ offset: { x: 0, y: 0 }, size: null })
  geomRef.current = { offset, size }

  const endGesture = () => {
    gesture.current = null
    window.removeEventListener("pointermove", onGestureMove)
    persist(geomRef.current.offset, geomRef.current.size)
  }

  const resetGeometry = () => {
    setOffset({ x: 0, y: 0 })
    setSize(null)
    setMaximized(false)
    if (persistId) { try { localStorage.removeItem(`win:${persistId}`) } catch { /* */ } }
  }

  const customized = maximized || size || offset.x !== 0 || offset.y !== 0

  const panelStyle = {}
  if (!minimized) {
    if (maximized) {
      panelStyle.width = "calc(100vw - 2rem)"
      panelStyle.height = "calc(100dvh - 2rem)"
      panelStyle.maxWidth = "none"
      panelStyle.maxHeight = "none"
    } else {
      if (size) {
        panelStyle.width = `${size.w}px`
        panelStyle.height = `${size.h}px`
        panelStyle.maxWidth = "none"
        panelStyle.maxHeight = "none"
      }
      if (offset.x !== 0 || offset.y !== 0) {
        panelStyle.transform = `translate(${offset.x}px, ${offset.y}px)`
      }
    }
  }

  const guard = (userHandler) => (e) => {
    if (minimized || preventClose) e.preventDefault()
    userHandler?.(e)
  }

  return (
    <DialogPortal>
      <DialogOverlay className={minimized ? "!opacity-0 !pointer-events-none" : undefined}>
        <DialogPrimitive.Content
          ref={(node) => { panelRef.current = node; if (typeof ref === "function") ref(node); else if (ref) ref.current = node }}
          onOpenAutoFocus={(e) => {
            const isMobile = window.innerWidth < 768;
            if (isMobile) e.preventDefault();
            onOpenAutoFocus?.(e)
          }}
          onEscapeKeyDown={guard(onEscapeKeyDown)}
          onInteractOutside={guard(onInteractOutside)}
          onPointerDownOutside={guard(onPointerDownOutside)}
          style={{ ...panelStyle, ...(style || {}) }}
          className={cn(
            "relative z-[9999] w-full border shadow-xl duration-200",
            "bg-th-card text-cream-900 border-th-border",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "max-w-lg rounded-t-2xl rounded-b-none sm:rounded-2xl",
            "max-h-[92dvh] sm:max-h-[calc(100dvh-3rem)] flex flex-col",
            "border-border",
            minimized && "!invisible !h-0 !max-h-0 !overflow-hidden !border-0 !shadow-none",
            className
          )}
          {...props}>
          {/* Drag strip (desktop only) */}
          {!hideWindowControls && (
            <div
              onPointerDown={startDrag}
              className="hidden md:block absolute inset-x-0 top-0 h-10 cursor-move z-[5]"
              aria-hidden="true"
            />
          )}
          <span ref={(n) => { if (n) { const t = n.parentElement?.querySelector("h2"); titleRef.current = t?.textContent || "" } }} className="hidden" aria-hidden="true" />
          {children}
          {/* Window controls */}
          <div className={cn("absolute top-2.5 z-20 flex items-center gap-0.5", isRtl ? "left-2.5" : "right-2.5")}>
            {!hideWindowControls && customized && !maximized && (
              <button type="button" onClick={resetGeometry} aria-label="Reset window size"
                className="hidden md:inline-flex rounded-lg p-1.5 text-muted-foreground opacity-60 transition-all hover:opacity-100 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring">
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
            {!hideWindowControls && (
              <button type="button" onClick={() => win?.setMinimized(true)} aria-label="Minimize window"
                className="hidden md:inline-flex rounded-lg p-1.5 text-muted-foreground opacity-60 transition-all hover:opacity-100 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring">
                <Minus className="h-4 w-4" />
              </button>
            )}
            {!hideWindowControls && (
              <button type="button" onClick={() => setMaximized((m) => !m)} aria-label={maximized ? "Restore window size" : "Maximize window"}
                className="hidden md:inline-flex rounded-lg p-1.5 text-muted-foreground opacity-60 transition-all hover:opacity-100 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring">
                {maximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </button>
            )}
            <DialogPrimitive.Close
              disabled={preventClose}
              aria-label="Close"
              className="rounded-lg p-1.5 text-muted-foreground opacity-70 ring-offset-background transition-all hover:opacity-100 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-30">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>
          {/* Resize handle (desktop only) */}
          {!hideWindowControls && !maximized && (
            <div
              onPointerDown={startResize}
              aria-hidden="true"
              className={cn(
                "hidden md:block absolute bottom-0 w-4 h-4 z-20",
                isRtl ? "left-0 cursor-nesw-resize" : "right-0 cursor-nwse-resize"
              )}
            >
              <svg viewBox="0 0 16 16" className={cn("w-4 h-4 text-muted-foreground/40", isRtl && "scale-x-[-1]")} fill="none">
                <path d="M14 8 8 14M14 12l-2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogOverlay>
      {/* Minimized dock pill */}
      {minimized && (
        <button
          type="button"
          onClick={() => win?.setMinimized(false)}
          data-testid="dialog-restore-pill"
          className={cn(
            "fixed bottom-4 z-[10000] flex items-center gap-2 rounded-full border border-th-border bg-th-card px-4 py-2.5 shadow-lg text-sm font-medium text-cream-900 hover:shadow-xl transition-shadow",
            isRtl ? "left-4" : "right-4"
          )}
        >
          <ChevronUp className="h-4 w-4" />
          {titleRef.current || (isRtl ? "استعادة النافذة" : "Restore window")}
        </button>
      )}
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-start relative z-10",
      "px-4 sm:px-5 pt-4 sm:pt-5 pb-3",
      "flex-shrink-0 border-b border-border",
      className
    )}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-2",
      "px-4 sm:px-5 pb-4 sm:pb-5 pt-3",
      "flex-shrink-0 border-t border-border",
      className
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-base sm:text-lg font-semibold leading-none tracking-tight text-foreground", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
